/**
 * SVG renderer for natal chart wheel. Backend is single source of truth for angles and house cusps.
 *
 * Two separate coordinate systems (no global wheel rotation):
 * - lonToScreenZodiac(lon): outer zodiac ring only. Aries always at 12 o'clock. 0° = right, CCW. screenDeg = (90 - lon) mod 360.
 * - lonToScreenInner(lon, ascLon): inner wheel only (houses, axes, planets, aspects). ASC always at 9 o'clock (180°). screenDeg = (180 + (lon - ascLon)) mod 360.
 * Contract: chart.angles (ascLon, mcLon, dcLon, icLon). chart.houses[] has cusp longitudes per house 1..12.
 */

/** Single viewBox size and center (one coordinate system for all layers). */
const SIZE = 800;
const CX = SIZE / 2;
const CY = SIZE / 2;
/** Max radius so nothing draws outside the wheel (half of SIZE). */
const MAX_WHEEL_R = SIZE / 2;

/**
 * Clamp radius to [0, MAX_WHEEL_R] so no path can exceed wheel bounds.
 */
function clampRadius(r) {
  const n = Number(r);
  if (Number.isNaN(n) || !Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(MAX_WHEEL_R, n));
}

/**
 * Clamp a coordinate (x or y) to [-MAX_WHEEL_R, MAX_WHEEL_R].
 */
function clampCoord(v) {
  const n = Number(v);
  if (Number.isNaN(n) || !Number.isFinite(n)) return 0;
  return Math.max(-MAX_WHEEL_R, Math.min(MAX_WHEEL_R, n));
}

// Sign ring: stable pastel palette, Aries (0°) .. Pisces (330°). Does not change with theme.
const SIGN_COLORS = [
  '#E8B4B8', // Aries 0° - pastel red
  '#A8D5BA', // Taurus 30° - pastel green
  '#F5E6A3', // Gemini 60° - pastel yellow
  '#B5EAD7', // Cancer 90° - pastel mint
  '#FFDAC1', // Leo 120° - pastel peach
  '#C9C9FF', // Virgo 150° - pastel lavender
  '#FFB7B2', // Libra 180° - pastel pink
  '#B4E7CE', // Scorpio 210° - pastel sage
  '#FFDFD3', // Sagittarius 240° - pastel apricot
  '#D4E5F7', // Capricorn 270° - pastel blue
  '#E2F0CB', // Aquarius 300° - pastel lime
  '#FFF4E6', // Pisces 330° - pastel cream
];

const ZODIAC_SIGNS = [
  "Aries", "Taurus", "Gemini", "Cancer",
  "Leo", "Virgo", "Libra", "Scorpio",
  "Sagittarius", "Capricorn", "Aquarius", "Pisces"
];

// Sign abbreviations for display
const SIGN_ABBR = [
  "Ari", "Tau", "Gem", "Can",
  "Leo", "Vir", "Lib", "Sco",
  "Sag", "Cap", "Aqu", "Pis"
];

/**
 * Format ecliptic longitude to sign, degree, and minute
 * This is the single source of truth for planet degree labels
 * @param {number} lonDeg - Ecliptic longitude in degrees (0..360)
 * @returns {Object} { signIndex, signAbbr, deg, min, text }
 */
export function formatEclipticLongitude(lonDeg) {
  // Normalize to 0..360
  const norm = ((lonDeg % 360) + 360) % 360;
  
  // Calculate sign index (0-11)
  const signIndex = Math.floor(norm / 30);
  
  // Calculate degree within sign (0-29.999...)
  const degInSign = norm % 30;
  
  // Calculate degree (integer) and minute
  const deg = Math.floor(degInSign);
  const min = Math.floor((degInSign - deg) * 60);
  
  // Get sign abbreviation
  const signAbbr = SIGN_ABBR[signIndex] || '???';
  
  // Format text: "24°16′ Lib" (or "24° Lib" if min is 0)
  const text = min > 0 ? `${deg}°${min}′ ${signAbbr}` : `${deg}° ${signAbbr}`;
  
  return {
    signIndex,
    signAbbr,
    deg,
    min,
    text
  };
}

// Planet glyph mapping (Unicode symbols)
const PLANET_GLYPHS = {
  'sun': '☉',
  'moon': '☽',
  'mercury': '☿',
  'venus': '♀',
  'mars': '♂',
  'jupiter': '♃',
  'saturn': '♄',
  'uranus': '♅',
  'neptune': '♆',
  'pluto': '♇',
  'node': '☊',
  'northnode': '☊',
  'southnode': '☋',
  'chiron': '⚷',
  // Common variations
  'north node': '☊',
  'south node': '☋',
  'true node': '☊',
  'mean node': '☊',
};

function norm360(d) {
  return ((d % 360) + 360) % 360;
}

/** Return a minimal error SVG so the page does not go blank. */
function renderErrorSvg(message) {
  return `<svg xmlns="http://www.w3.org/2000/svg" data-wheel="true" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}"><rect x="0" y="0" width="100%" height="100%" fill="#fff"/><text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" fill="red" font-size="14">${message}</text></svg>`;
}

// Oinas (0°) ylös (12 o'clock), 0° screen oikealla, CCW positive. Käytössä VAIN data-layer="zodiac".
function lonToScreenZodiac(lon) {
  return norm360(90 - lon);
}

// ASC vasemmalle (9 o'clock). Käytössä VAIN data-layer="inner".
function lonToScreenInner(lon, ascLon) {
  return norm360(180 + (lon - ascLon));
}

/**
 * Screen angle to (x,y). 0° = right (3 o'clock), 90° = 12 o'clock (up), angle increases CCW.
 * @param {number} cx - Center x
 * @param {number} cy - Center y
 * @param {number} r - Radius
 * @param {number} screenDeg - Screen angle in degrees
 * @returns {{x: number, y: number}}
 */
function screenAngleToXY(cx, cy, r, screenDeg) {
  const rad = (screenDeg * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy - r * Math.sin(rad)
  };
}

/**
 * Longitude to (x,y) for zodiac ring only (center 0,0). Uses lonToScreenZodiac.
 */
function lonToCoordsZodiac(lon, radius) {
  const screenDeg = lonToScreenZodiac(norm360(lon));
  const rad = (screenDeg * Math.PI) / 180;
  const R = clampRadius(radius);
  return {
    x: clampCoord(R * Math.cos(rad)),
    y: clampCoord(-R * Math.sin(rad))
  };
}

/**
 * Longitude to (x,y) for inner wheel only (center 0,0). Uses lonToScreenInner(lon, ascLon).
 */
function lonToCoordsInner(lon, radius, ascLon) {
  const screenDeg = lonToScreenInner(norm360(lon), ascLon);
  const rad = (screenDeg * Math.PI) / 180;
  const R = clampRadius(radius);
  return {
    x: clampCoord(R * Math.cos(rad)),
    y: clampCoord(-R * Math.sin(rad))
  };
}

/**
 * Render sign ring: 12 colored segments (0° Aries..330° Pisces). Uses lonToScreenZodiac only; no ascLon.
 */
function renderSignRingSegments(rOuter, rInner) {
  const rO = clampRadius(rOuter);
  const rI = clampRadius(rInner);
  const parts = [];

  for (let i = 0; i < 12; i++) {
    const startLon = i * 30;
    const endLon = (i + 1) * 30;
    const start = lonToCoordsZodiac(startLon, rO);
    const end = lonToCoordsZodiac(endLon, rO);
    const innerStart = lonToCoordsZodiac(startLon, rI);
    const innerEnd = lonToCoordsZodiac(endLon, rI);
    const large = 0;
    const d = `M ${start.x} ${start.y} A ${rO} ${rO} 0 ${large} 1 ${end.x} ${end.y} L ${innerEnd.x} ${innerEnd.y} A ${rI} ${rI} 0 ${large} 0 ${innerStart.x} ${innerStart.y} Z`;
    parts.push(`<path d="${d}" fill="${SIGN_COLORS[i]}" stroke="#333" stroke-width="0.5"/>`);
  }
  return parts.join('\n');
}

/**
 * Render zodiac labels on the sign ring (center of each segment). Uses lonToScreenZodiac only.
 */
function renderZodiacLabels(rOuter, rInner) {
  const labels = [];
  const rO = clampRadius(rOuter);
  const rI = clampRadius(rInner);
  const labelRadius = clampRadius((rO + rI) / 2);

  for (let i = 0; i < 12; i++) {
    const midLon = (i + 0.5) * 30;
    const labelPos = lonToCoordsZodiac(midLon, labelRadius);
    labels.push(
      `<text x="${labelPos.x}" y="${labelPos.y}" text-anchor="middle" dominant-baseline="middle" font-size="14" font-weight="bold" fill="#000">${SIGN_ABBR[i]}</text>`
    );
  }
  return labels.join('\n');
}

/**
 * Render tick marks on the zodiac ring (sign boundaries at 0,30,...,330 and minor ticks). Uses lonToScreenZodiac only.
 */
function renderTicks(rInner) {
  const ticks = [];
  for (let i = 0; i < 12; i++) {
    const lon = i * 30;
    const start = lonToCoordsZodiac(lon, rInner);
    const end = lonToCoordsZodiac(lon, rInner - 10);
    ticks.push(
      `<line x1="${start.x}" y1="${start.y}" x2="${end.x}" y2="${end.y}" stroke="#333" stroke-width="2"/>`
    );
  }
  for (let i = 0; i < 360; i += 5) {
    if (i % 30 === 0) continue;
    const start = lonToCoordsZodiac(i, rInner);
    const end = lonToCoordsZodiac(i, rInner - 5);
    ticks.push(
      `<line x1="${start.x}" y1="${start.y}" x2="${end.x}" y2="${end.y}" stroke="#666" stroke-width="0.5" opacity="0.5"/>`
    );
  }
  return ticks.join('\n');
}

/** Etsii cuspit: chart.houses (number+lon) tai chart.houseCusps. Palauttaa 12 longea järjestyksessä 1..12. */
function getHouseCusps(chart) {
  if (!chart) return [];
  if (Array.isArray(chart.houseCusps) && chart.houseCusps.length === 12) {
    return chart.houseCusps.map((lon) => norm360(Number(lon)));
  }
  if (!Array.isArray(chart.houses) || chart.houses.length !== 12) return [];
  const sorted = [...chart.houses].sort((a, b) => (a.house ?? 0) - (b.house ?? 0));
  return sorted.map((h) => norm360(h.lon ?? h.longitude ?? h.degree ?? h.cuspLon ?? 0));
}

/**
 * Render house cusps and numbers from backend cusps. Uses lonToScreenInner only.
 * Each cusp line: angle = lonToScreenInner(cuspLon, ascLon), line from center to radius.
 * House number at midpoint between consecutive cusps (wrap: if next < current, next += 360).
 */
function renderHouses(chart, rHouseOuter, rHouseInner, ascLon) {
  const cusps = getHouseCusps(chart);
  if (cusps.length !== 12) return '';
  const houseLabelRadius = (rHouseInner + rHouseOuter) / 2;
  const elements = [];

  for (let i = 0; i < 12; i++) {
    const cuspLon = cusps[i];
    const innerPoint = lonToCoordsInner(cuspLon, rHouseInner, ascLon);
    const outerPoint = lonToCoordsInner(cuspLon, rHouseOuter, ascLon);
    elements.push(
      `<line x1="${innerPoint.x}" y1="${innerPoint.y}" x2="${outerPoint.x}" y2="${outerPoint.y}" stroke="#999" stroke-width="1" stroke-dasharray="3,2" opacity="0.6"/>`
    );
    const nextCuspLon = cusps[(i + 1) % 12];
    let startLon = cuspLon;
    let endLon = nextCuspLon;
    if (endLon < startLon) endLon += 360;
    const midLon = norm360((startLon + endLon) / 2);
    const labelPos = lonToCoordsInner(midLon, houseLabelRadius, ascLon);
    elements.push(
      `<text x="${labelPos.x}" y="${labelPos.y}" text-anchor="middle" dominant-baseline="middle" font-size="14" font-weight="bold" fill="#333">${i + 1}</text>`
    );
  }
  return elements.join('\n');
}

/** Akselit: viivat keskipisteestä radiusiin. Käyttää (0,0) keskipistettä (inner group). */
function renderAxesScreen(centerX, centerY, r, labelOffset, screen) {
  const R = clampRadius(r);
  const labelR = clampRadius(r + (labelOffset || 28));
  const axes = [
    { screenDeg: screen.ascScreen, label: 'ASC' },
    { screenDeg: screen.dcScreen, label: 'DC' },
    { screenDeg: screen.mcScreen, label: 'MC' },
    { screenDeg: screen.icScreen, label: 'IC' }
  ];
  const parts = [];
  for (const axis of axes) {
    const outer = screenAngleToXY(centerX, centerY, R, axis.screenDeg);
    const inner = { x: centerX, y: centerY };
    parts.push(`<line x1="${inner.x}" y1="${inner.y}" x2="${outer.x}" y2="${outer.y}" stroke="#FFD700" stroke-width="3" opacity="0.9"/>`);
    const labelPos = screenAngleToXY(centerX, centerY, labelR, axis.screenDeg);
    parts.push(`<text x="${labelPos.x}" y="${labelPos.y}" text-anchor="middle" dominant-baseline="middle" font-size="13" font-weight="bold" fill="#000">${axis.label}</text>`);
  }
  return parts.join('\n');
}

/**
 * Get planet glyph from key or use provided glyph
 * @param {string} key - Planet key (e.g., 'sun', 'moon', 'Sun', 'Moon')
 * @param {string} providedGlyph - Glyph from chart data
 * @returns {string} Unicode glyph symbol or fallback text
 */
function getPlanetGlyph(key, providedGlyph) {
  // Use provided glyph if it's valid (not empty circle)
  if (providedGlyph && providedGlyph !== '○' && providedGlyph.trim() !== '') {
    return providedGlyph;
  }
  
  // Normalize key: lowercase, trim, remove spaces
  const normalizedKey = (key || '').toLowerCase().trim().replace(/\s+/g, ' ');
  
  // Try exact match first
  if (PLANET_GLYPHS[normalizedKey]) {
    return PLANET_GLYPHS[normalizedKey];
  }
  
  // Try without spaces
  const noSpaceKey = normalizedKey.replace(/\s+/g, '');
  if (PLANET_GLYPHS[noSpaceKey]) {
    return PLANET_GLYPHS[noSpaceKey];
  }
  
  // Fallback: use first 2 letters of key (uppercase) if unknown
  if (key && key.length >= 2) {
    return key.substring(0, 2).toUpperCase();
  }
  
  // Last resort: return empty circle
  return '○';
}

/**
 * Render planet glyphs with degree labels (base ecliptic coords)
 * @param {Object} chart - Chart data
 * @param {number} rPlanet - Planet radius (inside zodiac ring)
 * @returns {string} SVG string
 */
function renderPlanets(chart, rPlanet, ascLon) {
  const elements = [];
  
  const sortedPlanets = [...chart.planets].sort((a, b) => {
    const lonA = a.lon ?? a.longitude ?? 0;
    const lonB = b.lon ?? b.longitude ?? 0;
    return lonA - lonB;
  });
  
  const groups = [];
  let currentGroup = [];
  
  for (let i = 0; i < sortedPlanets.length; i++) {
    const planet = sortedPlanets[i];
    const lon = planet.lon ?? planet.longitude ?? 0;
    
    if (currentGroup.length === 0) {
      currentGroup.push({ planet, lon });
    } else {
      const lastLon = currentGroup[currentGroup.length - 1].lon;
      const diff = Math.abs(lon - lastLon);
      const wrapDiff = Math.min(diff, 360 - diff);
      
      if (wrapDiff < 6) {
        currentGroup.push({ planet, lon });
      } else {
        groups.push(currentGroup);
        currentGroup = [{ planet, lon }];
      }
    }
  }
  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }
  
  for (const group of groups) {
    for (let i = 0; i < group.length; i++) {
      const { planet, lon } = group[i];
      
      const truePos = lonToCoordsInner(lon, rPlanet, ascLon);
      const radialOffset = i * 8;
      const planetRadius = rPlanet + radialOffset;
      const offsetPos = lonToCoordsInner(lon, planetRadius, ascLon);
      
      // Draw thin connector line from true position to offset position (if offset)
      if (radialOffset > 0) {
        elements.push(
          `<line x1="${truePos.x}" y1="${truePos.y}" x2="${offsetPos.x}" y2="${offsetPos.y}" stroke="#999" stroke-width="0.5" opacity="0.4" stroke-dasharray="2,2"/>`
        );
      }
      
      const glyph = getPlanetGlyph(planet.key, planet.glyph);
      const planetName = (planet.nameFi || planet.name || planet.key || '').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      elements.push(
        `<g><title>${planetName}</title><text x="${offsetPos.x}" y="${offsetPos.y}" text-anchor="middle" dominant-baseline="middle" font-size="20" fill="#000" font-weight="bold">${glyph}</text></g>`
      );
      
      const degreeOffset = 20;
      const degreePos = lonToCoordsInner(lon, planetRadius + degreeOffset, ascLon);
      
      // Use unified formatter: derive from ecliptic longitude (source of truth)
      const formatted = formatEclipticLongitude(lon);
      // For wheel display, show just degree and minute (no sign abbreviation to keep it clean)
      const degreeLabel = formatted.min > 0 ? `${formatted.deg}°${formatted.min}′` : `${formatted.deg}°`;
      // Add retrograde indicator if planet is retrograde
      const isRetrograde = planet.retrograde === true || planet.retro === true;
      
      // Position degree text centered below glyph (clearly readable: 12-13px)
      elements.push(
        `<text x="${degreePos.x}" y="${degreePos.y}" text-anchor="middle" dominant-baseline="middle" font-size="13" fill="#000" font-weight="600">${degreeLabel}</text>`
      );
      
      // Retrograde "R" indicator in red, same size as degree text
      if (isRetrograde) {
        const retroPos = lonToCoordsInner(lon, planetRadius + degreeOffset + 8, ascLon);
        elements.push(
          `<text x="${retroPos.x}" y="${retroPos.y}" text-anchor="middle" dominant-baseline="middle" font-size="13" fill="#FF0000" font-weight="bold">R</text>`
        );
      }
    }
  }
  
  return elements.join('\n');
}

/**
 * Compute aspects between planets (client-side fallback)
 * @param {Array} planets - Array of planet objects with lon and key
 * @returns {Array} Array of aspect objects {aKey, bKey, type, orb}
 */
function computeAspects(planets) {
  const aspects = [];
  const aspectTypes = [
    { angle: 0, orb: 8, name: 'conjunction' },
    { angle: 180, orb: 8, name: 'opposition' },
    { angle: 120, orb: 7, name: 'trine' },
    { angle: 90, orb: 7, name: 'square' },
    { angle: 60, orb: 6, name: 'sextile' },
  ];
  
  for (let i = 0; i < planets.length; i++) {
    for (let j = i + 1; j < planets.length; j++) {
      const p1 = planets[i];
      const p2 = planets[j];
      
      if (!p1 || !p1.lon || !p2 || !p2.lon) continue;
      
      // Compute smallest angular distance d in [0..180]
      const diff = Math.abs(p1.lon - p2.lon);
      const d = Math.min(diff, 360 - diff);
      
      // Check each aspect type
      for (const aspectType of aspectTypes) {
        if (Math.abs(d - aspectType.angle) <= aspectType.orb) {
          aspects.push({
            aKey: p1.key,
            bKey: p2.key,
            type: aspectType.name,
            orb: aspectType.orb
          });
          break; // Only one aspect per pair
        }
      }
    }
  }
  
  return aspects;
}

/**
 * Render aspect lines between planets (base ecliptic coords).
 * Straight <line> only; endpoints at rPlanet (inner wheel radius). Never extend outside wheel.
 * @param {Object} chart - Chart data
 * @param {number} rPlanet - Planet radius (clamped; aspects connect only inside wheel)
 * @param {number} ascLon - Ascendant longitude (for lon→screen)
 * @returns {string} SVG string
 */
function renderAspects(chart, rPlanet, ascLon) {
  if (!Array.isArray(chart.planets) || chart.planets.length === 0) {
    return '';
  }
  const planetMap = new Map();
  for (const planet of chart.planets) {
    if (planet && planet.key && planet.lon != null) {
      planetMap.set(planet.key, planet);
    }
  }
  
  let aspectsToRender = [];
  
  // If chart.aspects exists and has length > 0, use them
  if (Array.isArray(chart.aspects) && chart.aspects.length > 0) {
    aspectsToRender = chart.aspects;
  } else {
    // Otherwise, compute aspects client-side from planet longitudes
    aspectsToRender = computeAspects(chart.planets);
  }
  
  if (aspectsToRender.length === 0) {
    return '';
  }
  
  const lines = [];
  const drawnPairs = new Set(); // Prevent duplicate lines
  
  // Render aspect lines
  for (const aspect of aspectsToRender) {
    // Handle both formats: {p1, p2} or {aKey, bKey}
    const key1 = aspect.p1 || aspect.aKey;
    const key2 = aspect.p2 || aspect.bKey;
    
    if (!key1 || !key2) continue;
    
    // Create unique pair identifier (sorted to avoid duplicates)
    const pairKey = [key1, key2].sort().join('-');
    if (drawnPairs.has(pairKey)) continue;
    drawnPairs.add(pairKey);
    
    const p1 = planetMap.get(key1);
    const p2 = planetMap.get(key2);
    
    if (!p1 || !p2) continue;

    // Normalize longitudes to [0, 360); straight <line> between planet points (no curves)
    const lon1 = norm360(p1.lon);
    const lon2 = norm360(p2.lon);
    const pos1 = lonToCoordsInner(lon1, rPlanet, ascLon);
    const pos2 = lonToCoordsInner(lon2, rPlanet, ascLon);

    // Thin grey lines like the reference
    lines.push(
      `<line x1="${pos1.x}" y1="${pos1.y}" x2="${pos2.x}" y2="${pos2.y}" stroke="#888" stroke-width="1.5" opacity="0.6"/>`
    );
  }
  
  return lines.join('\n');
}

/**
 * Render inner circle rings (house ring boundaries)
 * @param {number} rHouseRingOuter - Outer radius of house ring
 * @param {number} rHouseRingInner - Inner radius of house ring
 * @returns {string} SVG string
 */
function renderInnerRings(rHouseRingOuter, rHouseRingInner) {
  const rings = [];
  
  // Outer house ring circle
  rings.push(
    `<circle cx="0" cy="0" r="${rHouseRingOuter}" fill="none" stroke="#ccc" stroke-width="1" opacity="0.5"/>`
  );
  
  // Inner house ring circle
  rings.push(
    `<circle cx="0" cy="0" r="${rHouseRingInner}" fill="none" stroke="#ccc" stroke-width="1" opacity="0.5"/>`
  );
  
  return rings.join('\n');
}

/**
 * Main render function
 * @param {Object} chart - Chart data
 * @param {Object} opts - Options {size?: number}
 * @returns {string} SVG string
 */
export function renderWheelSvg(chart, opts = {}) {
  // Single source of truth: chart.angles only (ascLon, mcLon, dcLon, icLon). All references use these locals.
  const angles = chart?.angles ?? {};
  const ascLonNum = Number(angles.ascLon ?? angles.asc?.lon);
  const mcLonNum = Number(angles.mcLon ?? angles.mc?.lon);
  const dcLonNum = Number(angles.dcLon ?? angles.dc?.lon);
  const icLonNum = Number(angles.icLon ?? angles.ic?.lon);

  if (!Number.isFinite(ascLonNum)) {
    return renderErrorSvg('ASC longitude missing');
  }
  if (!Number.isFinite(mcLonNum)) {
    return renderErrorSvg('Missing mcLon');
  }

  const ascLon = norm360(ascLonNum);
  const mcLon = norm360(mcLonNum);
  const dcLon = Number.isFinite(dcLonNum) ? norm360(dcLonNum) : norm360(ascLon + 180);
  const icLon = Number.isFinite(icLonNum) ? norm360(icLonNum) : norm360(mcLon + 180);

  const vb = SIZE;
  const cx = CX;
  const cy = CY;

  const defaultLayers = ['zodiac', 'axes', 'houses', 'planets', 'aspects'];
  const enabled = opts.enabledLayers != null ? new Set(opts.enabledLayers) : new Set(defaultLayers);

  const outerR = clampRadius(MAX_WHEEL_R);
  const ringW  = 40;
  const innerR = clampRadius(outerR - ringW);
  const houseR = clampRadius(innerR - 40);
  const planetR = clampRadius(houseR - 25);
  
  if (!Array.isArray(chart.houses) || chart.houses.length !== 12) {
    return renderErrorSvg('Chart error: Invalid houses data');
  }
  if (!Array.isArray(chart.planets) || chart.planets.length === 0) {
    return renderErrorSvg('Chart error: No planets data');
  }

  const cusps = getHouseCusps(chart);
  if (process.env.NODE_ENV !== 'production') {
    if (lonToScreenInner(ascLon, ascLon) !== 180) {
      // eslint-disable-next-line no-console
      console.error('[CHART] lonToScreenInner(ascLon, ascLon) !== 180');
    }
    if (lonToScreenZodiac(0) !== 90) {
      // eslint-disable-next-line no-console
      console.error('[CHART] lonToScreenZodiac(0) !== 90');
    }
    // eslint-disable-next-line no-console
    console.log('[CHART_DEV]', { ascLon, mcLon, dcLon, icLon, firstCusp: cusps[0], secondCusp: cusps[1] });
  }

  const ascScreenDeg = lonToScreenInner(ascLon, ascLon);
  const dcScreenDeg = lonToScreenInner(dcLon, ascLon);
  const mcScreenDeg = lonToScreenInner(mcLon, ascLon);
  const icScreenDeg = lonToScreenInner(icLon, ascLon);

  const maxR = clampRadius(outerR);
  const innerRClamped = clampRadius(innerR);
  const rHouseOuter = clampRadius(houseR + 40);
  const rHouseInner = clampRadius(houseR);
  const axisRadius = maxR + 15;

  const zodiacContent = enabled.has('zodiac')
    ? `${renderSignRingSegments(maxR, innerRClamped)}\n${renderTicks(innerRClamped)}\n${renderZodiacLabels(maxR, innerRClamped)}`
    : '';
  const innerParts = [];
  innerParts.push(renderInnerRings(rHouseOuter, rHouseInner));
  if (enabled.has('houses')) innerParts.push(renderHouses(chart, rHouseOuter, rHouseInner, ascLon));
  if (enabled.has('aspects')) innerParts.push(renderAspects(chart, planetR, ascLon));
  if (enabled.has('planets')) innerParts.push(renderPlanets(chart, planetR, ascLon));
  if (enabled.has('axes')) {
    innerParts.push(renderAxesScreen(0, 0, axisRadius, 28, { ascScreen: ascScreenDeg, dcScreen: dcScreenDeg, mcScreen: mcScreenDeg, icScreen: icScreenDeg }));
  }
  const innerContent = innerParts.join('\n');

  const whitePlate = `<circle cx="0" cy="0" r="${maxR}" fill="#fff" stroke="none"/>`;
  const backgroundCircle = `<circle cx="0" cy="0" r="${maxR}" fill="none" stroke="#333" stroke-width="2"/>`;

  const fullSvg = `<svg xmlns="http://www.w3.org/2000/svg" data-wheel="true"
     width="${vb}"
     height="${vb}"
     viewBox="0 0 ${vb} ${vb}"
     preserveAspectRatio="xMidYMid meet"
     overflow="visible">
<rect x="0" y="0" width="100%" height="100%" fill="#fff"/>
<g class="wheel-root" transform="translate(${cx} ${cy})">
${whitePlate}
${backgroundCircle}
<g data-layer="zodiac">${zodiacContent}</g>
<g data-layer="inner">${innerContent}</g>
</g>
</svg>`;

  return fullSvg;
}
